// File: HistoryModal.js

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, TimeScale, Title, Tooltip, Legend } from 'chart.js';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';

// Register Chart.js components that are being used
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, TimeScale, Title, Tooltip, Legend, zoomPlugin);


// This is the component definition that was moved from App.js
const HistoryModal = ({ item, onClose, backendUrl, getAuthHeaders }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const chartRef = useRef(null);

    const [dateInputs, setDateInputs] = useState({
        start: new Date(new Date().getTime() - 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
        end: new Date().toISOString().slice(0, 16),
    });
    
    const [yMin, setYMin] = useState('');
    const [yMax, setYMax] = useState('');

    const [oldestDataTimestamp, setOldestDataTimestamp] = useState(null);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [isInitialZoomSet, setIsInitialZoomSet] = useState(false);


    
    const fetchMoreHistory = useCallback(async () => {
        if (isFetchingMore || !oldestDataTimestamp) return;

        setIsFetchingMore(true);
        setError('');

        const headers = getAuthHeaders();
        if (!headers) {
            setError('Authentication failed.');
            setIsFetchingMore(false);
            return;
        }

        try {
            const fetchEndDate = new Date(oldestDataTimestamp);
            const fetchStartDate = new Date(oldestDataTimestamp);
            fetchStartDate.setDate(fetchEndDate.getDate() - 30);

            const startUtc = fetchStartDate.toISOString();
            const endUtc = fetchEndDate.toISOString();

            const response = await fetch(
                `${backendUrl}?type=${item.type}&address=${item.address}&start=${startUtc}&end=${endUtc}`, {
                    headers: headers
                }
            );

            if (!response.ok) throw new Error('Failed to fetch more history.');

            const newData = await response.json();
            if (newData && newData.length > 0) {
                setHistory(prevHistory => [...newData, ...prevHistory]);
                setOldestDataTimestamp(fetchStartDate);
            } else {
                console.log("No more historical data available.");
            }

        } catch (err) {
            console.error("Failed to fetch more history", err);
            setError(err.message);
        } finally {
            setIsFetchingMore(false);
        }
    }, [isFetchingMore, oldestDataTimestamp, getAuthHeaders, backendUrl, item.type, item.address]);


    useEffect(() => {
        const fetchInitialHistory = async () => {
            const headers = getAuthHeaders();
            if (!headers) {
                setError('Authentication failed. No token found.');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError('');
                
                const fetchEndDate = new Date();
                const fetchStartDate = new Date();
                // --- CHANGE: Fetch last 24 hours initially instead of 30 days ---
                fetchStartDate.setDate(fetchEndDate.getDate() - 1);

                const startUtc = fetchStartDate.toISOString();
                const endUtc = fetchEndDate.toISOString();

                const response = await fetch(
                    `${backendUrl}?type=${item.type}&address=${item.address}&start=${startUtc}&end=${endUtc}`, {
                        headers: headers
                    }
                );

                if (!response.ok) {
                    throw new Error(`Server Error: ${response.status} ${response.statusText}`);
                }

                const contentType = response.headers.get("content-type");
                if (!contentType || !contentType.includes("application/json")) {
                    throw new TypeError("Backend did not send JSON. Received HTML or other format.");
                }
                
                const data = await response.json();
                setHistory(data || []);
                setOldestDataTimestamp(fetchStartDate);

                if (!data || data.length === 0) {
                    setError("No historical data found for this time range.");
                }

            } catch (err) {
                console.error("Failed to fetch history", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (item) {
            fetchInitialHistory();
        }
    }, [item, backendUrl, getAuthHeaders]);


    const handleResetView = () => {
        if (chartRef.current) {
            const newNow = new Date();
            const newTwentyFourHoursAgo = new Date(newNow.getTime() - 24 * 60 * 60 * 1000);
            setDateInputs({
                start: newTwentyFourHoursAgo.toISOString().slice(0, 16),
                end: newNow.toISOString().slice(0, 16),
            });
            setYMin('');
            setYMax('');
            chartRef.current.zoomScale('x', { min: newTwentyFourHoursAgo.getTime(), max: newNow.getTime() });
        }
    };

    const handleDateInputChange = (e) => {
        setDateInputs(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleApplyDateRange = () => {
        if (chartRef.current) {
            const min = new Date(dateInputs.start).getTime();
            const max = new Date(dateInputs.end).getTime();
            chartRef.current.zoomScale('x', { min, max });
        }
    };

    const chartData = {
        datasets: [{
            label: `${item.name} Value`,
            data: history.map(d => ({ x: new Date(d.time), y: d.value })),
            borderColor: '#61dafb',
            backgroundColor: 'rgba(97, 218, 251, 0.5)',
            borderWidth: 2,
            pointRadius: 1,
            stepped: item.type === 'coil',
            
        }],
    };

    const chartOptions = useMemo(() => {
        const handleZoomOrPan = ({ chart }) => {
            const threshold = 86400000; // One day in milliseconds
            if (oldestDataTimestamp && chart.scales.x.min < oldestDataTimestamp.getTime() + threshold) {
                fetchMoreHistory();
            }
        };

        return {
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            scales: {
                x: {
                    type: 'time',
                    time: {
                        tooltipFormat: 'MMM d, yyyy, h:mm:ss a',
                        displayFormats: {
                            hour: 'MMM d, h:mm a',
                            day: 'MMM d',
                            minute: 'h:mm a'
                        }
                    },
                    title: { display: true, text: 'Time', color: '#FFF' },
                    ticks: { color: '#CCC' },
                },
                y: {
                    title: { display: true, text: 'Value', color: '#FFF' },
                    min: yMin !== '' && !isNaN(yMin) ? Number(yMin) : undefined,
                    max: yMax !== '' && !isNaN(yMax) ? Number(yMax) : undefined,
                    ticks: { color: '#CCC' },
                }
            },
            plugins: {
                legend: { display: false },
                title: { display: true, text: `History for ${item.name} - Drag to pan, scroll to zoom`, color: '#FFF' },
                zoom: {
                    pan: {
                        enabled: true,
                        mode: 'x',
                        onPanComplete: handleZoomOrPan
                    },
                    zoom: {
                        wheel: { enabled: true },
                        pinch: { enabled: true },
                        mode: 'x',
                        onZoomComplete: handleZoomOrPan
                    },
                },
            },
        }
    }, [yMin, yMax, item.name, oldestDataTimestamp, fetchMoreHistory]);

    useEffect(() => {
        if (!loading && history.length > 0 && chartRef.current && !isInitialZoomSet) {
            const now = new Date();
            const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
            chartRef.current.zoomScale('x', { min: twentyFourHoursAgo.getTime(), max: now.getTime() });
            setIsInitialZoomSet(true);
        }
    }, [loading, history, isInitialZoomSet]);

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <button className="modal-close-button" onClick={onClose}>X</button>
                <h2>History for {item.name}</h2>
                
                <div className="history-controls">
                    <label>Start: <input type="datetime-local" name="start" value={dateInputs.start} onChange={handleDateInputChange} /></label>
                    <label>End: <input type="datetime-local" name="end" value={dateInputs.end} onChange={handleDateInputChange} /></label>
                    <button onClick={handleApplyDateRange}>Apply</button>
                    <span className="y-axis-label">Y-Axis:</span>
                    {/* --- FIX: Added id and name attributes to the number inputs --- */}
                    <label>Min: <input type="number" id="y-min-input" name="y-min-input" className="axis-input" value={yMin} onChange={e => setYMin(e.target.value)} placeholder="Auto" /></label>
                    <label>Max: <input type="number" id="y-max-input" name="y-max-input" className="axis-input" value={yMax} onChange={e => setYMax(e.target.value)} placeholder="Auto" /></label>
                    <button onClick={handleResetView}>Reset View</button>
                    {isFetchingMore && <span style={{color: '#61dafb', marginLeft: '10px'}}>Loading more...</span>}
                </div>

                {loading ? ( <p>Loading history...</p> ) : 
                error ? ( <p className="error">{error}</p> ) : 
                (
                    <>
                        <div className="chart-container-modal">
                            <Line ref={chartRef} options={chartOptions} data={chartData} />
                        </div>
                        <div className="history-table-container">
                            <table className="history-table">
                                <thead>
                                    <tr>
                                        <th>Timestamp</th>
                                        <th>Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.slice().reverse().map((entry, index) => (
                                        <tr key={index}>
                                            <td>{new Date(entry.time).toLocaleString()}</td>
                                            <td>{entry.value}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default HistoryModal;

